import { useState, useEffect, useCallback } from 'react';
import { api, ApiError } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/contexts/ToastContext';
import { Upload, Download, Trash2, FileText, Eye } from 'lucide-react';
import { ConfirmActionDialog } from '@/components/ui/confirm-action-dialog';

interface RateCardData {
  id: string;
  file_name: string;
  file_size: number;
  mime_type: string;
  uploaded_by: string;
  created_at: string;
  updated_at: string;
}

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
}

function fileExtLabel(mime: string): string {
  const map: Record<string, string> = {
    'application/pdf': 'PDF',
    'image/jpeg': 'JPEG',
    'image/png': 'PNG',
    'image/webp': 'WebP',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'XLSX',
    'application/vnd.ms-excel': 'XLS',
    'text/csv': 'CSV',
  };
  return map[mime] || 'File';
}

export default function RateCardPage() {
  const { hasPermission } = useAuth();
  const { addToast } = useToast();
  const canManage = hasPermission('rate_cards:manage');

  const [rateCard, setRateCard] = useState<RateCardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewMime, setPreviewMime] = useState<string>('');

  const loadRateCard = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get<{ data: RateCardData | null }>('/rate-cards');
      setRateCard(res.data || null);
    } catch (e: any) {
      setRateCard(null);
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadRateCard(); }, [loadRateCard]);

  async function handleUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.upload<{ success: boolean; data: { message: string } }>('/rate-cards', formData);
      addToast(res.data?.message || 'Rate card uploaded', 'success');
      loadRateCard();
    } catch (err: any) {
      addToast(err instanceof ApiError ? err.message : 'Upload failed', 'error');
    }
    setUploading(false);
  }

  async function handleDelete() {
    setDeleting(true);
    try {
      await api.delete('/rate-cards');
      addToast('Rate card deleted', 'success');
      setRateCard(null);
      setPreviewUrl(null);
    } catch (err: any) {
      addToast(err instanceof ApiError ? err.message : 'Delete failed', 'error');
    }
    setDeleting(false);
    setConfirmDelete(false);
  }

  async function handleDownload() {
    try {
      const response = await api.download('/rate-cards/download');
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = rateCard?.file_name || 'rate-card';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err: any) {
      addToast(err instanceof ApiError ? err.message : 'Download failed', 'error');
    }
  }

  async function handlePreview() {
    try {
      const response = await api.download('/rate-cards/download');
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      setPreviewUrl(url);
      setPreviewMime(rateCard?.mime_type || '');
    } catch (err: any) {
      addToast(err instanceof ApiError ? err.message : 'Preview failed', 'error');
    }
  }

  if (loading) {
    return (
      <div className="rate-card-page">
        <h1 className="text-2xl font-heading font-bold text-primary mb-6">Rate Card</h1>
        <Card><CardContent className="py-8 text-center text-secondary">Loading...</CardContent></Card>
      </div>
    );
  }

  return (
    <div className="rate-card-page">
      <div className="mb-6">
        <h1 className="text-2xl font-heading font-bold text-primary">Rate Card</h1>
        <p className="text-sm text-secondary mt-1">View and manage the organization's Rate Card.</p>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <FileText size={18} /> Current Rate Card
          </CardTitle>
          {canManage && (
            <label className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-sm font-medium cursor-pointer hover:bg-accent text-primary transition-colors">
              <Upload size={16} /> {rateCard ? 'Replace' : 'Upload'}
              <input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.webp,.xls,.xlsx,.csv"
                className="sr-only"
                disabled={uploading}
                onChange={handleUpload}
              />
            </label>
          )}
        </CardHeader>
        <CardContent>
          {!rateCard ? (
            <div className="text-center py-12">
              <FileText size={48} className="mx-auto text-muted-foreground mb-4" />
              <p className="text-secondary mb-2">No Rate Card uploaded yet.</p>
              {canManage && <p className="text-xs text-muted-foreground">Upload a PDF, image, Excel or CSV file.</p>}
            </div>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-xs text-secondary mb-1">File Name</p>
                  <p className="text-sm font-medium">{rateCard.file_name}</p>
                </div>
                <div>
                  <p className="text-xs text-secondary mb-1">Type</p>
                  <p className="text-sm font-medium">{fileExtLabel(rateCard.mime_type)}</p>
                </div>
                <div>
                  <p className="text-xs text-secondary mb-1">Size</p>
                  <p className="text-sm font-medium">{formatFileSize(rateCard.file_size)}</p>
                </div>
                <div>
                  <p className="text-xs text-secondary mb-1">Uploaded</p>
                  <p className="text-sm font-medium">{formatDate(rateCard.created_at)}</p>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-3 pt-2 border-t">
                <Button type="button" variant="outline" size="sm" onClick={handleDownload} className="min-h-11">
                  <Download size={16} className="mr-1" /> Download
                </Button>
                {(rateCard.mime_type === 'application/pdf' || rateCard.mime_type?.startsWith('image/')) && (
                  <Button type="button" variant="ghost" size="sm" onClick={handlePreview} className="min-h-11">
                    <Eye size={16} className="mr-1" /> Preview
                  </Button>
                )}
                {canManage && (
                  <Button type="button" variant="ghost" size="sm" onClick={() => setConfirmDelete(true)} className="text-red-500 hover:text-red-600 hover:bg-red-50 min-h-11">
                    <Trash2 size={16} className="mr-1" /> Delete
                  </Button>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {previewUrl && (
        <Card className="mt-4">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-base">Preview</CardTitle>
            <Button type="button" variant="ghost" size="sm" onClick={() => { URL.revokeObjectURL(previewUrl); setPreviewUrl(null); }}>
              Close Preview
            </Button>
          </CardHeader>
          <CardContent>
            {previewMime === 'application/pdf' ? (
              <iframe src={previewUrl} className="w-full border rounded-lg" style={{ height: '70vh' }} title="Rate Card Preview" />
            ) : previewMime?.startsWith('image/') ? (
              <img src={previewUrl} alt="Rate Card" className="max-w-full rounded-lg border" style={{ maxHeight: '70vh', objectFit: 'contain' }} />
            ) : (
              <p className="text-secondary text-sm">Preview not available for this file type. Please download instead.</p>
            )}
          </CardContent>
        </Card>
      )}

      <ConfirmActionDialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        title="Delete Rate Card"
        description="Are you sure you want to delete this Rate Card? This action cannot be undone."
        actionLabel="Delete"
        buttonStyle="red"
        loading={deleting}
        onConfirm={handleDelete}
      />
    </div>
  );
}
